import CustomBreadcrumb from '@/components/custom-breadcrumb/custom-breadcrumb';
import { SurveysHeader } from '@/components/surveys-header/surveys-header';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs/use-breadcrumbs';
import { Flex } from 'antd';
import styles from './surveys.module.scss';
import { SurveysTable } from '@/components/surveys-table/surveys-table';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import {
  DEFAULT_PAGINATION,
  MAX_PAGE_SIZE,
  useSortAndPagination,
} from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import { useSurveys } from '@/hooks/use-surveys/use-surveys';
import { useEffect, useState } from 'react';
import { SurveysFilters } from '@/services/surveys-service/surveys-service.types';
import { useUser } from '@/hooks/use-user/use-user';
import { useCompanyStore, useTimeRangeStore } from '@/store/app.store';
import { DEFAULT_TIME_RANGE } from '@/store/initial-time-range';
import dayjs, { Dayjs } from 'dayjs';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useExportSurveysHistory } from '@/hooks/use-export-surveys-history/use-export-surveys-history';
import {
  readUrlCount,
  readUrlNumber,
  readUrlString,
  resolveInitialTimeRange,
} from '@/utils/url-state/url-state';
import { useUrlSync } from '@/hooks/use-url-sync/use-url-sync';
import { useCompanyAccessGuard } from '@/hooks/use-company-access-guard/use-company-access-guard';
import { useInitialSearchParams } from '@/hooks/use-initial-search-params/use-initial-search-params';
import { useUrlCompanyToStoreSync } from '@/hooks/use-url-company-sync/use-url-company-sync';

export function Surveys() {
  const items = useBreadcrumbs();
  const { companyName, setCompanyName } = useCompanyStore();
  const { timeRange, startTime, endTime, setTimeRangeFilter } = useTimeRangeStore();
  const { userData } = useUser();
  const isCompanyAdmin = userData?.roles.find((role) => role.name === 'company_admin');
  const { companies } = useCompaniesList();
  const isAdmin = isCompanyAdmin && !!userData?.default_company;

  const initialUrl = useInitialSearchParams();

  const initialTimeFilter = resolveInitialTimeRange(
    initialUrl,
    timeRange,
    startTime,
    endTime,
    DEFAULT_TIME_RANGE
  );

  const [surveysFilters, setSurveysFilters] = useState<SurveysFilters>({
    time_range: initialTimeFilter.time_range as SurveysFilters['time_range'],
    company_name:
      readUrlString(initialUrl, 'company_name') ??
      companyName ??
      (isCompanyAdmin ? userData?.default_company : userData?.company?.name),
    start_time: initialTimeFilter.start_time,
    end_time: initialTimeFilter.end_time,
    status: readUrlString(initialUrl, 'status') as SurveysFilters['status'],
    score: readUrlNumber(initialUrl, 'score'),
    score_type: readUrlString(initialUrl, 'score_type'),
    query: readUrlString(initialUrl, 'query'),
    topic: readUrlString(initialUrl, 'topic'),
    conversation_type: readUrlString(initialUrl, 'conversation_type'),
    survey_category: readUrlString(initialUrl, 'survey_category') as SurveysFilters['survey_category'],
  });

  useUrlCompanyToStoreSync(initialUrl);

  useEffect(() => {
    if (readUrlString(initialUrl, 'company_name')) return;
    if (companyName) {
      setSurveysFilters((prev) => ({
        ...prev,
        company_name: companyName,
      }));
    } else if (isCompanyAdmin && userData?.default_company) {
      setSurveysFilters((prev) => ({
        ...prev,
        company_name: userData.default_company,
      }));
    }
  }, [companyName, isCompanyAdmin, userData?.default_company]);

  const { exportSurveysHistory, exportContextHolder } = useExportSurveysHistory({
    params: surveysFilters,
  });

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    initialTimeFilter.start_time && initialTimeFilter.end_time
      ? [dayjs(initialTimeFilter.start_time), dayjs(initialTimeFilter.end_time)]
      : null
  );

  const { paginationDetails, sortDetails, onPaginationChanged, onSortChange } =
    useSortAndPagination({
      defaultPagination: {
        page: readUrlCount(initialUrl, 'page') ?? DEFAULT_PAGINATION.page,
        items_per_page:
          readUrlCount(initialUrl, 'items_per_page', MAX_PAGE_SIZE) ??
          DEFAULT_PAGINATION.items_per_page,
      },
      defaultSorting: {
        sort_by: readUrlString(initialUrl, 'sort_by') ?? 'created_at',
        order_by: (readUrlString(initialUrl, 'order_by') ?? 'desc') as OrderBy,
      },
    });

  useCompanyAccessGuard(surveysFilters.company_name, companies, (allowedName) => {
    setSurveysFilters((prev) => ({ ...prev, company_name: allowedName }));
    setCompanyName(allowedName);
  });

  useUrlSync(
    {
      time_range: surveysFilters.time_range,
      start_time: surveysFilters.start_time,
      end_time: surveysFilters.end_time,
      company_name: surveysFilters.company_name,
      status: surveysFilters.status,
      score: surveysFilters.score,
      score_type: surveysFilters.score_type,
      query: surveysFilters.query,
      topic: surveysFilters.topic,
      conversation_type: surveysFilters.conversation_type,
      survey_category: surveysFilters.survey_category,
      page: paginationDetails.page,
      items_per_page: paginationDetails.items_per_page,
      sort_by: sortDetails?.sort_by,
      order_by: sortDetails?.order_by,
    },
    {
      defaults: {
        time_range: DEFAULT_TIME_RANGE,
        page: DEFAULT_PAGINATION.page,
        items_per_page: DEFAULT_PAGINATION.items_per_page,
        sort_by: 'created_at',
        order_by: 'desc',
      },
    }
  );

  const handleChange = (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => {
    onPaginationChanged(
      page || DEFAULT_PAGINATION.page,
      perPage || DEFAULT_PAGINATION.items_per_page
    );
    onSortChange(sortBy || 'created_at', orderBy || 'desc');
  };

  const {
    surveysData,
    refetch: refetchSurveys,
    isPending,
  } = useSurveys({
    filtersParams: {
      ...surveysFilters,
      company_name: surveysFilters.company_name,
      time_zone: timeZone,
    },
    paginationParams: { ...paginationDetails, ...sortDetails },
  });

  const handleRefreshData = () => {
    refetchSurveys();
  };

  const handleExportHistory = async () => {
    await exportSurveysHistory();
  };

  // A filter change invalidates the current offset: page 4 of the old result set is usually empty
  // in the new one.
  const handleCompanyChange = (filters: SurveysFilters) => {
    setSurveysFilters(filters);
    setCompanyName(filters.company_name ?? undefined);
    if (filters.time_range) {
      setTimeRangeFilter(filters.time_range);
    }
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const onClearDateRangeFilter = () => {
    setSurveysFilters((prevFilters) => ({
      ...prevFilters,
      time_range: '7d',
      start_time: undefined,
      end_time: undefined,
    }));
    setTimeRangeFilter('7d');
    setDates(null);
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const onChangeDateRangeFilter = (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => {
    setSurveysFilters((prevFilters) => ({
      ...prevFilters,
      time_range: undefined,
      start_time: filterValue.time_range.start_time,
      end_time: filterValue.time_range.end_time,
    }));
    setTimeRangeFilter(
      undefined,
      filterValue.time_range.start_time,
      filterValue.time_range.end_time
    );
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  return (
    <Flex vertical className={styles.container}>
      <CustomBreadcrumb items={items} />

      <div>
        <div>
          {exportContextHolder && <div key="export-message-holder">{exportContextHolder}</div>}
        </div>

        <SurveysHeader
          onSearchQueryChange={setCompanySearchQuery}
          searchResults={companySearchData}
          isSearchLoading={companySearchLoading}
          searchError={companySearchError}
          averageScore={surveysData?.avarage_score || 0}
          surveysNumber={surveysData?.surveys_count || 0}
          onRefreshData={handleRefreshData}
          filters={surveysFilters}
          onChangeFilters={handleCompanyChange}
          companiesList={companies?.map((company) => company.name) || []}
          dates={dates}
          setDates={setDates}
          onDateRangeClear={onClearDateRangeFilter}
          onChangeDateRangeFilter={onChangeDateRangeFilter}
          onExportHistory={handleExportHistory}
        />
        <SurveysTable
          currentPage={paginationDetails.page}
          onChange={handleChange}
          surveysData={surveysData?.surveys}
          isAdmin={isAdmin}
          pagination={surveysData?.pagination}
          loading={isPending}
        />
      </div>
    </Flex>
  );
}
